import { House, Search, Compass, UserRound } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@mondivago/supabase';
import { stripAllLanguagePrefixes } from '@/i18n/routingUtils';

export function MobileBottomNavbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, loading, isSupplier } = useAuth();

  const authReady = !loading && !!user;

  const isNavItemActive = (to: string, matchPaths?: string[]) => {
    // Strip language prefixes (e.g. /it-IT, /en-US) so active states work on localized URLs
    const path = stripAllLanguagePrefixes(location.pathname);
    const pathsToMatch = matchPaths && matchPaths.length > 0 ? matchPaths : [to];
    return pathsToMatch.some((candidate) => {
      if (candidate === '/') return path === '/';
      return path === candidate || path.startsWith(`${candidate}/`);
    });
  };

  return (
    <nav className="fixed inset-x-0 bottom-0 z-[70] md:hidden pointer-events-auto">
      <div className="bg-white border-t border-gray-200 pb-[env(safe-area-inset-bottom)]">
        <ul className="flex items-center justify-between px-1 py-1 sm:px-2">
          {[
            { to: '/', label: 'Home', icon: House, matchPaths: ['/'] },
            { to: '/search', label: 'Search', icon: Search, matchPaths: ['/search'] },
            { to: '/explore', label: 'Explore', icon: Compass, matchPaths: ['/explore'] },
            {
              to: authReady ? (isSupplier ? '/supplier/dashboard' : '/trips/bookings') : '/guest/login',
              label: authReady ? 'Profile' : 'Sign in',
              icon: UserRound,
              matchPaths: [
                '/supplier/dashboard',
                '/trips/bookings',
                '/trips/settings',
                '/trips/messages',
                '/guest/login',
              ],
            },
          ].map((item) => {
            const Icon = item.icon;
            const active = isNavItemActive(item.to, item.matchPaths);

            return (
              <li key={item.label} className="flex-1">
                <NavLink
                  to={item.to}
                  onClick={(e) => {
                    if (active) {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="relative flex w-full flex-col items-center justify-center gap-1 rounded-xl py-1.5 transition-all active:scale-95"
                >
                  <div
                    className={`flex h-[34px] w-[54px] items-center justify-center rounded-full transition-all duration-300 ${
                      active ? 'bg-lime-400 text-slate-950' : 'text-slate-500 hover:text-slate-900'
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 ${active ? 'scale-105' : ''} transition-transform`}
                      strokeWidth={active ? 2.5 : 2}
                    />
                  </div>
                  <span
                    className={`text-[10px] font-semibold transition-colors ${active ? 'text-slate-900 font-bold' : 'text-slate-500'}`}
                  >
                    {item.label === 'Saved'
                      ? t('nav.saved')
                      : item.label === 'Profile'
                        ? t('nav.profile')
                        : item.label === 'Sign in'
                          ? t('nav.login')
                          : t(`nav.${item.label.toLowerCase()}`, item.label)}
                  </span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
